<script setup lang="ts">
import type { ExportFormat } from "@/types";

import { ref } from "vue";

const props = defineProps<{
  searchText: string;
  searchBefore: boolean;
  searchAfter: boolean;
  onDelete: boolean;
  selectedCount: number;
  totalCount: number;
  addDisabled: boolean;
}>();

const emit = defineEmits<{
  (e: "update:searchText", value: string): void;
  (e: "update:searchBefore", value: boolean): void;
  (e: "update:searchAfter", value: boolean): void;
  (e: "open-dialog"): void;
  (e: "open-delete-confirm"): void;
  (e: "cancel-selection"): void;
  (e: "export", format: ExportFormat): void;
  (e: "export-selected", format: ExportFormat): void;
  (e: "open-import", file: File): void;
  (e: "open-import-from-guild"): void;
}>();

const fileInputRef = ref<HTMLInputElement | null>(null);

const onFileChange = (event: Event) => {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (!file) return;
  emit("open-import", file);
  (event.target as HTMLInputElement).value = "";
};
</script>

<template>
  <div class="p-dictionary__pc-header bg-background">
    <v-text-field
      :model-value="props.searchText"
      class="p-dictionary__search"
      prepend-inner-icon="mdi-magnify"
      placeholder="辞書を検索"
      variant="solo"
      hide-details
      @update:model-value="emit('update:searchText', $event)"
    />
    <div>
      <v-checkbox
        :model-value="props.searchBefore"
        class="p-dictionary__search-target"
        label="変換前"
        density="compact"
        color="blue-accent-2"
        hide-details
        @update:model-value="emit('update:searchBefore', !!$event)"
      />
      <v-checkbox
        :model-value="props.searchAfter"
        class="p-dictionary__search-target"
        label="変換後"
        density="compact"
        color="blue-accent-2"
        hide-details
        @update:model-value="emit('update:searchAfter', !!$event)"
      />
    </div>
    <v-spacer />
    <template v-if="props.onDelete">
      <v-chip
        class="mr-3"
        variant="tonal"
        prepend-icon="mdi-check-circle-outline"
      >
        {{ props.selectedCount }}件選択中
      </v-chip>
      <v-btn
        class="mr-2"
        color="danger"
        prepend-icon="mdi-trash-can"
        size="large"
        @click="emit('open-delete-confirm')"
      >
        削除
      </v-btn>
      <v-menu>
        <template #activator="{ props: menuProps }">
          <v-btn
            class="mr-2"
            prepend-icon="mdi-export"
            append-icon="mdi-menu-down"
            size="large"
            :disabled="props.selectedCount === 0"
            v-bind="menuProps"
          >
            エクスポート
          </v-btn>
        </template>
        <v-list>
          <v-list-item
            :title="`${props.selectedCount}件をエクスポート`"
            disabled
            class="text-caption text-grey"
          />
          <v-divider />
          <v-list-item
            title="CSV"
            @click="emit('export-selected', 'csv')"
          />
          <v-list-item
            title="TSV"
            @click="emit('export-selected', 'tsv')"
          />
          <v-list-item
            title="JSON"
            @click="emit('export-selected', 'json')"
          />
        </v-list>
      </v-menu>
      <v-btn
        color="grey"
        size="large"
        @click="emit('cancel-selection')"
      >
        キャンセル
      </v-btn>
    </template>
    <v-btn
      v-else
      color="success"
      prepend-icon="mdi-plus"
      size="large"
      :disabled="props.addDisabled"
      @click="emit('open-dialog')"
    >
      追加
    </v-btn>
    <template v-if="!props.onDelete">
      <v-menu>
        <template #activator="{ props: menuProps }">
          <v-btn
            class="ml-2"
            prepend-icon="mdi-export"
            append-icon="mdi-menu-down"
            size="large"
            :disabled="props.totalCount === 0"
            v-bind="menuProps"
          >
            エクスポート
          </v-btn>
        </template>
        <v-list>
          <v-list-item
            :title="`全${props.totalCount}件をエクスポート`"
            disabled
            class="text-caption text-grey"
          />
          <v-divider />
          <v-list-item
            title="CSV"
            @click="emit('export', 'csv')"
          />
          <v-list-item
            title="TSV"
            @click="emit('export', 'tsv')"
          />
          <v-list-item
            title="JSON"
            @click="emit('export', 'json')"
          />
        </v-list>
      </v-menu>
      <v-menu>
        <template #activator="{ props: menuProps }">
          <v-btn
            class="ml-2"
            prepend-icon="mdi-import"
            append-icon="mdi-menu-down"
            size="large"
            v-bind="menuProps"
          >
            インポート
          </v-btn>
        </template>
        <v-list>
          <v-list-item
            title="他のサーバーから"
            @click="emit('open-import-from-guild')"
          />
          <v-list-item
            title="ファイルを選択"
            @click="fileInputRef?.click()"
          >
            <template #subtitle>
              <span class="text-caption">TXT / CSV / TSV / JSON に対応</span>
            </template>
          </v-list-item>
        </v-list>
      </v-menu>
      <input
        ref="fileInputRef"
        type="file"
        accept=".csv,.tsv,.txt,.json,text/csv,text/plain,text/tab-separated-values,application/json"
        style="display: none"
        @change="onFileChange"
      />
    </template>
  </div>
</template>
